// Places API (New) - REST only. We call the autocomplete + details endpoints
// directly from the browser and render our own dropdown, so there's NO Maps
// JavaScript SDK and no map: only the "Places API (New)" needs to be enabled.
//
// The key (VITE_GOOGLE_PLACES_API_KEY) is exposed client-side by design and MUST
// be HTTP-referrer + API restricted in the Google Cloud console. These calls go
// browser -> Google, never through our backend/Jarts, so they don't touch our
// compute concurrency; billing is per autocomplete session.

export const PLACES_API_KEY: string | undefined = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

const BASE = "https://places.googleapis.com/v1";

/** A new session groups a "type -> pick" into one billed Autocomplete session. */
export function newSessionToken(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

export interface Prediction {
  placeId: string;
  main: string; // primary line, e.g. "Tampa"
  secondary: string; // context line, e.g. "FL, USA"
  isCountry: boolean;
}

/** POST /places:autocomplete - country/region/city suggestions for `input`. */
export async function autocompletePlaces(
  input: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<Prediction[]> {
  if (!PLACES_API_KEY || !input.trim()) return [];
  const res = await fetch(`${BASE}/places:autocomplete`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Goog-Api-Key": PLACES_API_KEY },
    // `(regions)` = countries, admin areas, localities, postal codes (geographic,
    // not businesses) - the same set the buyer's market region cares about.
    body: JSON.stringify({ input, sessionToken, includedPrimaryTypes: ["(regions)"] }),
    signal,
  });
  if (!res.ok) throw new Error(`autocomplete ${res.status}`);
  const data = await res.json();
  return (data.suggestions || [])
    .map((s: any) => s.placePrediction)
    .filter(Boolean)
    .map((p: any) => ({
      placeId: p.placeId,
      main: p.structuredFormat?.mainText?.text || p.text?.text || "",
      secondary: p.structuredFormat?.secondaryText?.text || "",
      isCountry: (p.types || []).includes("country"),
    }));
}

export interface ResolvedPlace {
  region: string; // ISO-3166 alpha-2 (market_region)
  city: string; // formatted area, "" for a country-only pick (market_city)
}

/** GET /places/{id} - resolve a picked prediction to {region, city}. The country
 *  component gives the alpha-2 code; a country-only pick yields an empty city. */
export async function resolvePlace(
  placeId: string,
  sessionToken: string,
): Promise<ResolvedPlace | null> {
  if (!PLACES_API_KEY) return null;
  const res = await fetch(
    `${BASE}/places/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(sessionToken)}`,
    {
      headers: {
        "X-Goog-Api-Key": PLACES_API_KEY,
        "X-Goog-FieldMask": "addressComponents,formattedAddress,types",
      },
    },
  );
  if (!res.ok) throw new Error(`details ${res.status}`);
  const place = await res.json();
  const components: any[] = place.addressComponents || [];
  const country = components.find((c) => (c.types || []).includes("country"));
  const region = (country?.shortText || "").toUpperCase().slice(0, 2);
  if (!region) return null;
  const isCountryOnly = (place.types || []).includes("country");
  const city = isCountryOnly ? "" : place.formattedAddress || "";
  return { region, city };
}
